import { observer } from 'mobx-react-lite'
import Cropper, { type Area } from 'react-easy-crop'
import React, { useState } from 'react'
import { Icon, Slider, SpinnerLoader } from 'shared/ui'
import { type CropImageStore } from 'shared/ui/CropImage'
import styles from './styles.module.scss'

type ICropImageProps = {
  store: CropImageStore
}

export const CropImage = observer(({ store }: ICropImageProps) => {
  const { imageUrl, file, setCroppedAreaPixels, cropSize } = store
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(1)
  const [isReady, setIsReady] = useState(false)

  const onMediaLoaded = (mediaSize: { width: number; height: number }) => {
    const { width, height } = mediaSize
    const initialZoom = Math.max(cropSize / width, cropSize / height)

    setMinZoom(initialZoom)
    setZoom(initialZoom)
    setIsReady(true)
  }

  const onCropComplete = (_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }

  const onSliderZoomChange = (zoom: number | number[]) => {
    if (!Array.isArray(zoom)) setZoom(zoom)
  }

  if (!imageUrl || !file) return <SpinnerLoader ariaLabel={'CropImage'} />

  return (
    <div
      style={{
        width: `${cropSize}px`,
      }}
    >
      <div
        style={{
          height: `${cropSize}px`,
          position: 'relative',
          visibility: isReady ? 'visible' : 'hidden',
        }}
      >
        <Cropper
          image={imageUrl}
          crop={crop}
          zoom={zoom}
          cropShape={'round'}
          minZoom={minZoom}
          maxZoom={minZoom + 3}
          cropSize={{ width: cropSize, height: cropSize }}
          showGrid={false}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
          onMediaLoaded={onMediaLoaded}
          classes={{
            cropAreaClassName: styles.cropArea,
          }}
        />
      </div>

      <div className={styles.controls}>
        <Icon icon={'minusSquare'} fontSize={20} color={'var(--content-primary-tertiary)'} />
        <Slider
          value={zoom}
          min={minZoom}
          max={minZoom + 3}
          step={0.01}
          aria-labelledby='Zoom'
          onChange={(e, zoom) => onSliderZoomChange(zoom)}
          activeRailBackgroundColor={'var(--background-neutral-primary)'}
        />
        <Icon icon={'plusSquare'} fontSize={20} color={'var(--content-primary-tertiary)'} />
      </div>
    </div>
  )
})
